/**
 * The places of Dale HQ and which one a path belongs to. The sidebar, the tab
 * bar of the phone and the "back" button all answer from here.
 */

export type HqSectionId = 'inicio' | 'clientes' | 'finanzas' | 'dale-ai' | 'equipo' | 'organizaciones' | 'pagos';
export type HqIconName =
  | 'inicio'
  | 'clientes'
  | 'finanzas'
  | 'ia'
  | 'equipo'
  | 'organizaciones'
  | 'pagos'
  | 'mas'
  | 'buscar'
  | 'luna'
  | 'sol'
  | 'derecha'
  | 'izquierda'
  | 'abajo'
  | 'agregar'
  | 'cerrar'
  | 'enviar'
  | 'salir';

export type HqPlace = {
  id: HqSectionId;
  to: '/admin' | '/admin/clientes' | '/admin/finanzas' | '/admin/dale-ai' | '/admin/equipo' | '/admin/organizaciones' | '/admin/pagos';
  label: string;
  icon: HqIconName;
};

export const HQ_PRIMARY: readonly HqPlace[] = [
  { id: 'inicio', to: '/admin', label: 'Inicio', icon: 'inicio' },
  { id: 'clientes', to: '/admin/clientes', label: 'Clientes', icon: 'clientes' },
  { id: 'finanzas', to: '/admin/finanzas', label: 'Finanzas', icon: 'finanzas' },
  { id: 'dale-ai', to: '/admin/dale-ai', label: 'Dale AI', icon: 'ia' },
  { id: 'equipo', to: '/admin/equipo', label: 'Equipo', icon: 'equipo' },
];

/** What the admin already had before HQ: kept as places of their own, below the main five. */
export const HQ_SECONDARY: readonly HqPlace[] = [
  { id: 'organizaciones', to: '/admin/organizaciones', label: 'Organizaciones', icon: 'organizaciones' },
  { id: 'pagos', to: '/admin/pagos', label: 'Pagos por confirmar', icon: 'pagos' },
];

/** The tab bar of the phone: four places and a "Más" with the rest. */
export const HQ_TABS: readonly { id: HqSectionId | 'mas'; label: string; icon: HqIconName; to?: HqPlace['to'] }[] = [
  ...HQ_PRIMARY.slice(0, 4).map(({ id, label, icon, to }) => ({ id, label, icon, to })),
  { id: 'mas', label: 'Más', icon: 'mas' },
];

/** Where the phone's "Más" leads: what the tab bar has no room for. */
export const HQ_MORE: readonly HqPlace[] = [...HQ_PRIMARY.slice(4), ...HQ_SECONDARY];

export function activeSection(pathname: string): HqSectionId | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin') return 'inicio';
  if (path === '/admin/clientes' || path.startsWith('/admin/negocios')) return 'clientes';
  if (path === '/admin/finanzas') return 'finanzas';
  if (path === '/admin/dale-ai') return 'dale-ai';
  if (path === '/admin/equipo') return 'equipo';
  if (path === '/admin/organizaciones' || path.startsWith('/admin/organizaciones/')) return 'organizaciones';
  if (path === '/admin/pagos') return 'pagos';
  return null;
}

/** Where "Volver" goes from a detail page: the list it hangs from. Nothing on a place of its own. */
export function backPlace(pathname: string): '/admin/clientes' | '/admin/organizaciones' | null {
  if (pathname.startsWith('/admin/negocios/')) return '/admin/clientes';
  if (pathname.startsWith('/admin/organizaciones/')) return '/admin/organizaciones';
  return null;
}

/** The two letters of an avatar: the first letter of the first two words. */
export function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
  return letters || '?';
}
